import { useEffect, useState } from "react";
import client from "@/lib/glossyApi";
import { X, FileText, ExternalLink, User, Building, Phone, Mail, MapPin, Globe, Instagram, MessageSquareQuote } from "lucide-react";

const Row = ({ icon: Icon, label, value, testid, multi }) => {
  const shown = String(value || "").trim();
  return (
    <div className="border-b border-ink/8 py-3.5 grid grid-cols-[140px_1fr] gap-4 items-start" data-testid={testid}>
      <p className="overline text-ink/50 flex items-center gap-2 pt-0.5"><Icon size={12} /> {label}</p>
      {shown
        ? <p className={`font-sans text-sm text-ink ${multi ? "whitespace-pre-wrap leading-relaxed" : "break-words"}`}>{shown}</p>
        : <p className="font-mono text-[10px] text-ink/30 uppercase tracking-widest pt-1">— blank</p>}
    </div>
  );
};

/**
 * Read-only preview of a member's onboarding form submission.
 * Shows exactly the fields the member typed into /portal.
 * Used from the Studio header (member.user_id present) so the editor can see
 * what the member sent in verbatim, without editing.
 */
export default function MemberFormPreview({ member, onClose }) {
  const [questions, setQuestions] = useState([]);
  useEffect(() => {
    client.get("/members/me/interview-questions").then((r) => setQuestions(r.data.questions || [])).catch(() => {});
  }, []);
  if (!member) return null;
  const created = member.created_at ? new Date(member.created_at) : null;
  const updated = member.updated_at ? new Date(member.updated_at) : null;
  const answers = member.interview_answers || {};

  return (
    <div className="fixed inset-0 z-[100] bg-ink/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-8 overflow-y-auto" onClick={onClose} data-testid="member-form-preview-backdrop">
      <div className="bg-cream w-full max-w-3xl my-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="member-form-preview">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-ink/10 bg-ink text-cream">
          <div className="flex items-center gap-3 min-w-0">
            <FileText size={16} />
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-widest text-cream/60">Member onboarding · submitted verbatim</p>
              <p className="font-serif text-xl truncate">{member.business_name || "Untitled"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-cream/70 hover:text-cream" data-testid="member-form-preview-close"><X size={18} /></button>
        </div>

        <div className="px-6 py-3 border-b border-ink/10 bg-white flex flex-wrap items-center gap-2 text-[11px] font-mono text-ink/50">
          {created && <span>Submitted {created.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>}
          {updated && created && updated.getTime() !== created.getTime() && <span>· Last edited {updated.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>}
        </div>

        <div className="px-6 py-4 max-h-[65vh] overflow-y-auto" data-testid="member-form-preview-body">
          <Row icon={Building} label="Business name" value={member.business_name} testid="pf-business_name" />
          <Row icon={User} label="Owner name" value={member.owner_name} testid="pf-owner_name" />
          <Row icon={MapPin} label="Borough / area" value={member.borough} testid="pf-borough" />
          <Row icon={Building} label="Category" value={member.category} testid="pf-category" />
          <Row icon={Building} label="Business category" value={member.business_category} testid="pf-business_category" />
          <Row icon={MapPin} label="Full address" value={member.address} testid="pf-address" />
          <Row icon={Globe} label="Website" value={member.website} testid="pf-website" />
          <Row icon={Globe} label="Online shop" value={member.shop_url} testid="pf-shop_url" />
          <Row icon={Mail} label="Email" value={member.email} testid="pf-email" />
          <Row icon={Phone} label="Mobile" value={member.mobile} testid="pf-mobile" />
          <Row icon={Phone} label="Landline" value={member.landline} testid="pf-landline" />
          <Row icon={Instagram} label="Instagram" value={member.instagram} testid="pf-instagram" />
          <Row icon={Instagram} label="Facebook" value={member.facebook} testid="pf-facebook" />
          <Row icon={Instagram} label="TikTok" value={member.tiktok} testid="pf-tiktok" />
          <Row icon={Instagram} label="LinkedIn" value={member.linkedin} testid="pf-linkedin" />
          <Row icon={FileText} label="About the owner" value={member.personal_bio} testid="pf-personal_bio" multi />
          <Row icon={FileText} label="About the business" value={member.business_bio} testid="pf-business_bio" multi />
          <Row icon={FileText} label="Services / products" value={member.services} testid="pf-services" multi />
          <Row icon={FileText} label="Reader offer" value={member.offers} testid="pf-offers" multi />
          <Row icon={FileText} label="Links & notes to the desk" value={member.ref_notes} testid="pf-ref_notes" multi />
          <div className="pt-4 pb-1" data-testid="pf-interview">
            <p className="overline text-ink/50 flex items-center gap-2 mb-1"><MessageSquareQuote size={12} /> Their interview answers</p>
            {questions.map((q, i) => (
              <div key={i} className="border-b border-ink/8 py-3" data-testid={`pf-interview-${i}`}>
                <p className="font-sans text-xs text-ink/55 mb-1">{i + 1}. {q}</p>
                {String(answers[String(i)] ?? answers[i] ?? "").trim()
                  ? <p className="font-sans text-sm text-ink whitespace-pre-wrap leading-relaxed">{String(answers[String(i)] ?? answers[i]).trim()}</p>
                  : <p className="font-mono text-[10px] text-ink/30 uppercase tracking-widest">— blank</p>}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-ink/10 flex items-center justify-between gap-3 bg-white">
          <a href="/portal?preview=1" target="_blank" rel="noreferrer" className="overline text-ink/60 border border-ink/25 px-4 py-2 flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors" data-testid="member-form-preview-see-template">
            <ExternalLink size={13} /> See empty form template
          </a>
          <button onClick={onClose} className="bg-ink text-cream px-5 py-2 overline hover:bg-ink/90 transition-colors" data-testid="member-form-preview-done">Done</button>
        </div>
      </div>
    </div>
  );
}
